import React from 'react';

import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LinkIcon from '@mui/icons-material/Link';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import TitleIcon from '@mui/icons-material/Title';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import type { OnMount } from '@monaco-editor/react';

import { EditorViewMode, ViewModeToggle } from 'components/editor/ViewModeToggle';

/** Monaco editor インスタンス型を @monaco-editor/react のコールバックから導出する */
type CodeEditor = Parameters<OnMount>[0];

type Props = {
  /** Monaco editor インスタンスの取得関数 */
  getEditor: () => CodeEditor | null;
  viewMode: EditorViewMode;
  onViewModeChange: (mode: EditorViewMode) => void;
  /** ツールバーを非表示にする */
  onHide?: () => void;
};

/**
 * Markdown 編集用ツールバー。一般的な md 記法の挿入ボタンと、
 * 右端に表示モード切替（エディタ / 分割 / プレビュー）を置く。
 */
export const MarkdownToolbar: React.FC<Props> = ({
  getEditor,
  viewMode,
  onViewModeChange,
  onHide,
}) => {
  /** 選択範囲を before/after で囲む */
  const wrap = (before: string, after: string) => {
    const ed = getEditor();
    if (!ed) return;
    const sel = ed.getSelection();
    const model = ed.getModel();
    if (!sel || !model) return;
    const text = model.getValueInRange(sel);
    ed.executeEdits('md-toolbar', [
      { range: sel, text: `${before}${text || ''}${after}`, forceMoveMarkers: true },
    ]);
    ed.focus();
  };

  /** 現在行の先頭に prefix を挿入する（見出し・引用・リスト用） */
  const prefixLine = (prefix: string) => {
    const ed = getEditor();
    if (!ed) return;
    const sel = ed.getSelection();
    const model = ed.getModel();
    if (!sel || !model) return;
    const lineNumber = sel.startLineNumber;
    const range = {
      startLineNumber: lineNumber,
      startColumn: 1,
      endLineNumber: lineNumber,
      endColumn: 1,
    };
    ed.executeEdits('md-toolbar', [{ range, text: prefix, forceMoveMarkers: true }]);
    ed.focus();
  };

  /** カーソル位置にテキストを挿入 */
  const insert = (text: string) => {
    const ed = getEditor();
    if (!ed) return;
    const sel = ed.getSelection();
    if (!sel) return;
    ed.executeEdits('md-toolbar', [{ range: sel, text, forceMoveMarkers: true }]);
    ed.focus();
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}
    >
      <Tooltip title="見出し">
        <IconButton size="small" onClick={() => prefixLine('## ')}>
          <TitleIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="太字">
        <IconButton size="small" onClick={() => wrap('**', '**')}>
          <FormatBoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="斜体">
        <IconButton size="small" onClick={() => wrap('*', '*')}>
          <FormatItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="取り消し線">
        <IconButton size="small" onClick={() => wrap('~~', '~~')}>
          <StrikethroughSIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="インラインコード">
        <IconButton size="small" onClick={() => wrap('`', '`')}>
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="コードブロック">
        <IconButton size="small" onClick={() => insert('\n```\n\n```\n')}>
          <DataObjectIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="リンク">
        <IconButton size="small" onClick={() => wrap('[', '](url)')}>
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="引用">
        <IconButton size="small" onClick={() => prefixLine('> ')}>
          <FormatQuoteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="箇条書き">
        <IconButton size="small" onClick={() => prefixLine('- ')}>
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="番号付きリスト">
        <IconButton size="small" onClick={() => prefixLine('1. ')}>
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1 }} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
      {onHide && (
        <Tooltip title="ツールバーを隠す">
          <IconButton size="small" onClick={onHide} sx={{ ml: 0.5 }}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default MarkdownToolbar;
